import { useMemo } from "react"
import PropTypes from "prop-types"
import { chain, map, find, keys, omitBy } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { DATASET, UUID, ROLE } from "helpers/shapes"
import { makeDatasetElementRender, makeSourcesElementRender } from "helpers/datasets"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function TokenDatasetEntitlements({ account, role, tokenId, entitlements, datasets, update, tabs }) {
  const hasWriteAccess = role === "admin"

  const optionLabels = useMemo(() => chain(datasets)
    .orderBy("name", "asc")
    .reduce((datasetsById, dataset) => ({
      ...datasetsById,
      [dataset.id]: dataset.name,
    }), {})
    .value(),
  [datasets])

  // Only direct entitlements are important here
  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(entitlements, entitlement => entitlement.id === id && entitlement.direct)),
  [entitlements, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($tokenIds: [Id!]){
        ${map(data.datasets, (datasetId, index) => `dataset${index}: grantDatasetAccess(id: "${datasetId}", tokenIds: $tokenIds) { id }`)}
      }`

      const variables = { tokenIds: [tokenId] }

      update(query, variables, account, {
        reloadData: ["tokenDatasetEntitlements", "userDatasets", "models", "datasetEntitlements"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-datasets-token",
        payload: ({ datasets }) => ({
          datasets,
          tokenId,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "datasets",
        label: "Datasets",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, tokenId, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($tokenIds: [Id!]){
        ${map(data, (dataset, index) => `dataset${index}: revokeDatasetAccess(id: "${dataset.id}", tokenIds: $tokenIds) { id }`)}
      }`

      const variables = { tokenIds: [tokenId] }

      update(query, variables, account, {
        reloadData: ["tokenDatasetEntitlements", "userDatasets", "models", "datasetEntitlements"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-datasets-token",
        payload: (dataset) => ({
          dataset: dataset.id,
          tokenId,
        }),
      },
      // Only direct entitlements can be removed
      isEnabled: (dataset) => dataset.direct && hasWriteAccess,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, tokenId, update])

  // This will always be true because we cannot reach this page via the UI if entitlements are not enabled
  const entitlementsEnabled = true
  // Same functions for all table elements
  const getNameElement = makeDatasetElementRender(account, role, entitlementsEnabled, 400)
  const getSourcesElement = makeSourcesElementRender(account)

  return <div className="modules_token-dataset-entitlements">
    <UpsellTabs id="token-dataset-entitlements" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Token dataset entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={entitlements}
        columns={[{
          name: "Name",
          id: "name",
          render: getNameElement,
        },
        {
          name: "Sources",
          id: "sources",
          render: getSourcesElement,
        }]}
      />
    </Paper>
  </div>
}

TokenDatasetEntitlements.propTypes = {
  account: PropTypes.string.isRequired,
  tokenId: UUID,
  userId: UUID,
  role: ROLE.isRequired,
  entitlements: PropTypes.arrayOf(DATASET),
  datasets: PropTypes.arrayOf(DATASET).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
